import { useContext, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { toast } from 'react-toastify';
import { Button, Tab, Tabs, TmdxSwitch, Spinner, useDisclosure } from '@ds';
import { FaInfoCircle, FaPlus } from 'react-icons/fa';
import {
  ReleaseNoteDetails,
  ReleaseNoteProduct,
  RELEASE_NOTE_PRODUCT_CONFIG,
  RELEASE_NOTE_PRODUCT_LABELS,
  getPrimaryPlatform,
  groupReleasesByPlatform,
  sortByVersionDesc,
  sortPlatformKeys,
} from '@features/components/ReleaseNotes';
import { AuthContext } from '@lp/core/auth';
import { ReleaseNoteFormModal } from '@lp/features/modals';
import { ReleaseNote } from '@/api/apis/release-notes';
import { retrieveUnifiedError } from '@/api/utils';
import { ReleaseNotesPermissions } from '@/permissions';
import { ReleaseNotePlatformGroup } from './components';
import { useReleaseNoteDeleteMutation, useReleaseNotesByProductQuery } from './hooks';

export const ReleaseNotes = () => {
  const { checkPermission } = useContext(AuthContext);
  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure();

  const getDefaultExpandedPlatforms = (product: ReleaseNoteProduct): Set<string> => {
    const primaryPlatform = getPrimaryPlatform(RELEASE_NOTE_PRODUCT_CONFIG[product].platforms);
    return new Set<string>(primaryPlatform ? [primaryPlatform] : []);
  };

  const [activeTab, setActiveTab] = useState<ReleaseNoteProduct>(ReleaseNoteProduct.TxcAccess);
  const [selectedReleaseId, setSelectedReleaseId] = useState<string | null>(null);
  const [releaseToEdit, setReleaseToEdit] = useState<ReleaseNote | undefined>(undefined);
  const [isPreviewMode, setIsPreviewMode] = useState(true);
  const [expandedPlatforms, setExpandedPlatforms] = useState<Set<string>>(() => getDefaultExpandedPlatforms(activeTab));

  const hasManagePermission = checkPermission(ReleaseNotesPermissions.Write);
  const isEditMode = hasManagePermission && !isPreviewMode;

  const { data: releaseNotes, isLoading } = useReleaseNotesByProductQuery(activeTab);
  const { mutateAsync: deleteReleaseNoteAsync } = useReleaseNoteDeleteMutation(activeTab);

  const handleTabChange = (key: string | number) => {
    setActiveTab(key as ReleaseNoteProduct);
    setSelectedReleaseId(null);
    setExpandedPlatforms(getDefaultExpandedPlatforms(key as ReleaseNoteProduct));
  };

  const items = useMemo(() => {
    const rawItems = releaseNotes?.items ?? [];
    return [...rawItems].sort(sortByVersionDesc);
  }, [releaseNotes]);

  const groupedReleases = useMemo(() => groupReleasesByPlatform(items), [items]);

  const platformKeys = useMemo(() => sortPlatformKeys(Object.keys(groupedReleases)), [groupedReleases]);

  const defaultRelease = useMemo(() => {
    const [defaultPlatform] = platformKeys;
    return defaultPlatform ? groupedReleases[defaultPlatform][0] : undefined;
  }, [platformKeys, groupedReleases]);

  const selectedRelease = useMemo(() => {
    if (selectedReleaseId) {
      return items.find((item) => item.id === selectedReleaseId);
    }
    return defaultRelease;
  }, [items, selectedReleaseId, defaultRelease]);

  useEffect(() => {
    if (!defaultRelease) {
      setSelectedReleaseId(null);
      return;
    }

    const isSelectedInItems = selectedReleaseId && items.some((item) => item.id === selectedReleaseId);
    if (!isSelectedInItems) {
      setSelectedReleaseId(defaultRelease.id);
      setExpandedPlatforms((prev) => new Set(prev).add(defaultRelease.platform.toLowerCase()));
    }
  }, [items, selectedReleaseId, defaultRelease]);

  const handleTogglePlatform = (platform: string) => {
    setExpandedPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(platform)) {
        next.delete(platform);
      } else {
        next.add(platform);
      }
      return next;
    });
  };

  const handleSelectRelease = (releaseId: string) => {
    setSelectedReleaseId(releaseId);
  };

  const handleOpenCreate = () => {
    setReleaseToEdit(undefined);
    onFormOpen();
  };

  const handleOpenEdit = (release: ReleaseNote) => {
    setReleaseToEdit(release);
    onFormOpen();
  };

  const handleCloseFormModal = () => {
    setReleaseToEdit(undefined);
    onFormClose();
  };

  const handleDeleteRelease = async (release: ReleaseNote): Promise<boolean> => {
    try {
      await deleteReleaseNoteAsync(release.id);

      const remainingItems = items.filter((item) => item.id !== release.id);
      if (remainingItems.length > 0 && selectedReleaseId === release.id) {
        setSelectedReleaseId(remainingItems[0].id);
      } else if (remainingItems.length === 0) {
        setSelectedReleaseId(null);
      }

      toast.success('Release note deleted successfully');
      return true;
    } catch (e) {
      const error = retrieveUnifiedError(e as Error);
      toast.error(error.message || 'Failed to delete release note');
      return false;
    }
  };

  return (
    <div className="bg-base-80 min-h-screen p-6 flex flex-col">
      <div className="flex items-center mb-8">
        <h1 className="text-3xl text-base-5 mr-4">What's New</h1>
        {hasManagePermission && (
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3 bg-base-70 rounded-lg px-4 py-2">
              <span className={cn('text-sm font-medium', { 'text-base-5': isEditMode, 'text-base-30': !isEditMode })}>
                Edit Mode
              </span>
              <TmdxSwitch
                isSelected={isEditMode}
                onValueChange={() => setIsPreviewMode((prev) => !prev)}
                aria-label="Toggle edit mode"
                size="sm"
              />
            </div>
            <Button
              variant="solid"
              size="md"
              startContent={<FaPlus />}
              onPress={handleOpenCreate}
              aria-label="Add release note"
              className={cn({ invisible: !isEditMode })}
            >
              Add Release Note
            </Button>
            {isEditMode && (
              <div className="flex items-center gap-2 bg-yellow-100 border border-yellow-400 rounded-lg px-3 py-2">
                <FaInfoCircle className="text-yellow-600" size={14} />
                <span className="text-xs text-yellow-800">Please inform team if you are going to make changes.</span>
              </div>
            )}
          </div>
        )}
      </div>

      <Tabs
        aria-label="Release notes access tabs"
        variant="underlined"
        selectedKey={activeTab}
        onSelectionChange={handleTabChange}
        classNames={{
          tabContent: 'text-s-cps px-4',
          base: 'w-full mb-6 border-b border-base-60',
          tabList: 'p-0 gap-8',
        }}
      >
        {Object.entries(RELEASE_NOTE_PRODUCT_LABELS).map(([key, label]) => (
          <Tab key={key} title={label} />
        ))}
      </Tabs>

      <ReleaseNoteFormModal
        isOpen={isFormOpen}
        onClose={handleCloseFormModal}
        product={activeTab}
        releaseNote={releaseToEdit}
      />

      {isLoading && (
        <div className="flex-1 flex items-center justify-center">
          <Spinner />
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-base-20 text-l-r">No release notes available.</p>
        </div>
      )}

      {!isLoading && items.length > 0 && (
        <div className="flex flex-1">
          <aside className="min-w-[270px] bg-base-70 rounded-l-lg">
            <h4 className="font-semibold p-4">Releases</h4>
            <nav>
              {platformKeys.map((platform) => (
                <ReleaseNotePlatformGroup
                  key={platform}
                  platform={platform}
                  releases={groupedReleases[platform]}
                  isExpanded={expandedPlatforms.has(platform)}
                  isEditMode={isEditMode}
                  selectedReleaseId={selectedReleaseId}
                  onToggle={handleTogglePlatform}
                  onSelect={handleSelectRelease}
                  onEdit={handleOpenEdit}
                  onDelete={handleDeleteRelease}
                />
              ))}
            </nav>
          </aside>

          <ReleaseNoteDetails release={selectedRelease} />
        </div>
      )}
    </div>
  );
};
